import { Link } from "@inertiajs/react";
import { Upload, Download, CheckCircle2, FileText } from "lucide-react";
import Steps, { Step } from "@/components/help/Steps";
import Callout from "@/components/help/Callout";
import Screenshot from "@/components/help/Screenshot";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import StudentNameGuidance from "@/components/schools/StudentNameGuidance";
import ContactLink from "@/components/ContactLink";

export default function ImportingRosterCsv() {
  return (
    <>
      <p>
        Adding your students is the next step in the setup guide after Square is connected. For more than about 30 students, the fastest way is to upload a spreadsheet — we'll create the classrooms, add the students, and make activation codes for everyone in one go. (Got just a handful? You can <Link href="/help/articles/adding-classrooms-manually">type them in by hand</Link> instead.)
      </p>

      <h2>How it works</h2>
      <Steps>
        <Step title="Download the template">
          <p>
            In the setup guide's <strong>Add your students</strong> step (or on <strong>Classrooms</strong>), click <strong>Import a class list</strong>, then <strong>Download the template</strong>. You'll get a starter spreadsheet with the right columns already in place.
          </p>
          <Screenshot caption="Template download">
            <Card>
              <CardContent className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <FileText className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium text-foreground">classrooms_template.csv</p>
                    <p className="text-xs text-muted-foreground">First name, last name, classroom, grade</p>
                  </div>
                </div>
                <Button variant="outline" size="sm"><Download className="h-4 w-4" /> Download</Button>
              </CardContent>
            </Card>
          </Screenshot>
        </Step>
        <Step title="Fill it in">
          <p>
            Open it in Excel or Google Sheets. You'll see four columns: first name, last name, classroom, and grade. One row per student. The classroom name is whatever your families would recognize — "Room 12" or "Mrs. Lee," either works.
          </p>
          <StudentNameGuidance />
        </Step>
        <Step title="Upload it back">
          <p>
            Drag your saved file onto the upload area. You don't need to wait around — we'll do the work in the background and show you a summary when we're done.
          </p>
          <Screenshot caption="The upload area">
            <Card>
              <CardContent className="p-6 text-center space-y-3">
                <Upload className="h-8 w-8 text-muted-foreground/60 mx-auto" />
                <div>
                  <p className="text-sm font-medium text-foreground">Drop your file here, or click to choose</p>
                  <p className="text-xs text-muted-foreground mt-1">Up to 5,000 students</p>
                </div>
                <Button variant="outline">Choose file</Button>
              </CardContent>
            </Card>
          </Screenshot>
        </Step>
        <Step title="Check the summary">
          <p>
            We'll tell you how many students and classrooms got added. If anything looked off — a missing name, a typo in a classroom name — we'll flag it. Fix it in your spreadsheet and upload again. It's safe to do this as many times as you need.
          </p>
          <Screenshot caption="The summary">
            <Alert>
              <CheckCircle2 className="h-4 w-4 text-success" />
              <AlertTitle>All done</AlertTitle>
              <AlertDescription>
                <ul className="text-sm mt-1 space-y-0.5">
                  <li>184 students added</li>
                  <li>9 classrooms added</li>
                  <li>2 things to double-check</li>
                </ul>
              </AlertDescription>
            </Alert>
          </Screenshot>
        </Step>
      </Steps>

      <Callout variant="note" title="Last names are shortened before saving">
        <p>
          Use full first and last names in the spreadsheet. PagePledge shortens last names for privacy: Batke becomes Bat, Baxter becomes Bax, Lee becomes Le, and Li becomes L. A one-character last name is hidden entirely. If two students in a class have the same shortened name, we add a number to tell them apart. Full last names are never saved or shown after submission.
        </p>
      </Callout>

      <Callout variant="note" title="Two students with the same full name">
        <p>
          Include one row for each child, even if the rows have identical names and classrooms. Two Tom Lees become “Tom L.” and “Tom L-2.”, each with their own activation code and donation page. Check for accidental extra rows before uploading.
        </p>
        <p>
          Uploading the same roster again keeps both existing students. To add another child with that exact name later, include all students with that name and classroom in the CSV, or use Add student on the classroom page. If you upload only one row for that name, PagePledge assumes it's a student already in the class.
        </p>
      </Callout>

      <Callout variant="tip" title="Uploading again is safe">
        <p>
          To add new students, upload their rows. If a new student has the same full name as someone already in the class, include every child in that class with that name, as explained above. Uploading the same file again won't add students twice, and leaving a child out won't delete them. Existing activation codes, donations, and reading minutes stay with the same students.
        </p>
      </Callout>

      <Callout variant="note" title="Your plan's student limit">
        <p>
          Once your campaign is paid for, its plan covers a set number of students (the limits are on the <a href="/pricing">pricing page</a>). An upload that would take you past it is turned down whole, and nothing is saved. <ContactLink>Contact us</ContactLink> to upgrade. Before you pay there's no limit; the price follows how many students you've added. See <Link href="/help/articles/paying-for-your-campaign">Paying for your campaign</Link>.
        </p>
      </Callout>

      <Callout variant="note" title="Teachers come later">
        <p>
          The spreadsheet is just for students. Adding teachers to their classrooms is a separate step — see <Link href="/help/articles/inviting-teachers">Inviting teachers</Link>.
        </p>
      </Callout>

      <h2>What's next</h2>
      <ul>
        <li><Link href="/help/articles/inviting-teachers">Invite your teachers</Link> — the next step in the setup guide.</li>
        <li><Link href="/help/articles/printing-take-home-sheets">Printing take-home sheets</Link>.</li>
        <li><Link href="/help/articles/adding-classrooms-manually">Adding a classroom or student by hand</Link>.</li>
      </ul>
    </>
  );
}
